import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import Icon from '../../components/AppIcon';
import KanbanColumn from './components/KanbanColumn';
import ProspectCard from './components/ProspectCard';
import FilterBar from './components/FilterBar';
import AnalyticsSidebar from './components/AnalyticsSidebar';
import ProspectDetailModal from './components/ProspectDetailModal';
import Button from '../../components/ui/Button';
import { authService } from '../../services/authService';


const SalesPipelineKanbanBoard = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    dealSize: 'all',
    industry: 'all',
    assignedTo: 'all',
    timeInStage: 'all'
  });
  const [draggedProspect, setDraggedProspect] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await authService?.getCurrentSession();
        if (!session) {
          navigate('/admin-portal-login');
        }
      } catch (error) {
        navigate('/admin-portal-login');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>);

  }

  // Initialize mock prospect data
  useEffect(() => {
    const mockProspects = [
    {
      id: 1,
      companyName: 'TechCorp Solutions Inc.',
      contactName: 'Sarah Johnson',
      contactEmail: 'sarah.j@techcorp.com',
      avatar: 'https://images.unsplash.com/photo-1721240608299-cdf5ee26c235',
      avatarAlt: 'Modern glass office building exterior with blue sky reflecting in windows',
      status: 'demo-scheduled',
      dealValue: 850000,
      teamSize: '51-100',
      hrBudget: '500k-1m',
      industry: 'Technology',
      assignedTo: 'Admin User',
      priority: 'high',
      stageEntryDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)?.toISOString(),
      nextAction: 'Demo call scheduled for Jan 15, 2026',
      probability: 65,
      notes: [
      {
        id: 1,
        content: 'Initial contact made. Very interested in executive recruitment features.',
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)?.toISOString(),
        createdBy: 'Admin User'
      }]

    },
    {
      id: 2,
      companyName: 'BuildRight Construction',
      contactName: 'Robert Martinez',
      contactEmail: 'r.martinez@buildright.com',
      avatar: 'https://images.unsplash.com/photo-1685978085094-996ac4bd1748',
      avatarAlt: 'Construction site with yellow crane against blue sky and modern building framework',
      status: 'demo-scheduled',
      dealValue: 450000,
      teamSize: '21-50',
      hrBudget: '250k-500k',
      industry: 'Construction',
      assignedTo: 'Admin User',
      priority: 'medium',
      stageEntryDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)?.toISOString(),
      nextAction: 'Follow-up after demo materials sent',
      probability: 50,
      notes: []
    },
    {
      id: 3,
      companyName: 'InnovateTech Systems',
      contactName: 'Emily Chen',
      contactEmail: 'e.chen@innovatetech.com',
      avatar: 'https://images.unsplash.com/photo-1663817131635-8e04e87e5d76',
      avatarAlt: 'Modern glass office building with reflective windows showing city skyline',
      status: 'in-negotiation',
      dealValue: 1200000,
      teamSize: '101-250',
      hrBudget: '1m-2m',
      industry: 'Technology',
      assignedTo: 'Admin User',
      priority: 'high',
      stageEntryDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
      nextAction: 'Pricing proposal review meeting',
      probability: 75,
      notes: [
      {
        id: 2,
        content: 'Demo went very well. Discussed custom integration requirements.',
        createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)?.toISOString(),
        createdBy: 'Admin User'
      },
      {
        id: 3,
        content: 'Sent pricing proposal. Waiting for budget approval from CFO.',
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)?.toISOString(),
        createdBy: 'Admin User'
      }]

    },
    {
      id: 4,
      companyName: 'Premier Properties Group',
      contactName: 'Michael Thompson',
      contactEmail: 'm.thompson@premierproperties.com',
      avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_180f6eda5-1766560944336.png',
      avatarAlt: 'Luxury residential building exterior with modern architecture and landscaped entrance',
      status: 'in-negotiation',
      dealValue: 650000,
      teamSize: '51-100',
      hrBudget: '500k-1m',
      industry: 'Real Estate',
      assignedTo: 'Admin User',
      priority: 'medium',
      stageEntryDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)?.toISOString(),
      nextAction: 'Contract review with legal team',
      probability: 70,
      notes: []
    },
    {
      id: 5,
      companyName: 'Global Finance Corp',
      contactName: 'Lisa Anderson',
      contactEmail: 'l.anderson@globalfinance.com',
      avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_17faa8e7a-1763295754680.png',
      avatarAlt: 'Professional headshot of African American woman with curly hair wearing burgundy blazer',
      status: 'closed-won',
      dealValue: 950000,
      teamSize: '101-250',
      hrBudget: '1m-2m',
      industry: 'Finance',
      assignedTo: 'Admin User',
      priority: 'high',
      stageEntryDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)?.toISOString(),
      nextAction: 'Onboarding kickoff meeting',
      probability: 100,
      notes: [
      {
        id: 4,
        content: 'Contract signed! Moving to implementation phase.',
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)?.toISOString(),
        createdBy: 'Admin User'
      }]

    }];

    setProspects(mockProspects);
  }, []);

  // Calculate time in stage
  const calculateTimeInStage = (stageEntryDate) => {
    const now = new Date();
    const entry = new Date(stageEntryDate);
    const diffDays = Math.floor((now - entry) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter prospects
  const getFilteredProspects = (status) => {
    return prospects?.filter((prospect) => {
      if (prospect?.status !== status) return false;

      if (filters?.dealSize !== 'all') {
        if (filters?.dealSize === 'small' && prospect?.dealValue >= 500000) return false;
        if (filters?.dealSize === 'medium' && (prospect?.dealValue < 500000 || prospect?.dealValue >= 1000000)) return false;
        if (filters?.dealSize === 'large' && prospect?.dealValue < 1000000) return false;
      }

      if (filters?.industry !== 'all' && prospect?.industry !== filters?.industry) return false;
      if (filters?.assignedTo !== 'all' && prospect?.assignedTo !== filters?.assignedTo) return false;

      if (filters?.timeInStage !== 'all') {
        const daysInStage = calculateTimeInStage(prospect?.stageEntryDate);
        if (filters?.timeInStage === 'new' && daysInStage > 3) return false;
        if (filters?.timeInStage === 'active' && (daysInStage <= 3 || daysInStage > 14)) return false;
        if (filters?.timeInStage === 'stale' && daysInStage <= 14) return false;
      }

      return true;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (prospect) => {
    setDraggedProspect(prospect);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleDrop = (newStatus) => {
    if (draggedProspect && draggedProspect?.status !== newStatus) {
      const updatedProspects = prospects?.map((p) => {
        if (p?.id === draggedProspect?.id) {
          return {
            ...p,
            status: newStatus,
            stageEntryDate: new Date()?.toISOString()
          };
        }
        return p;
      });
      setProspects(updatedProspects);
      setDraggedProspect(null);
    }
  };

  const handleViewDetails = (prospect) => {
    setSelectedProspect(prospect);
    setShowDetailModal(true);
  };

  const handleUpdateProspect = (updatedProspect) => {
    const updatedProspects = prospects?.map((p) =>
    p?.id === updatedProspect?.id ? updatedProspect : p
    );
    setProspects(updatedProspects);
    setSelectedProspect(updatedProspect);
  };

  const columns = [
  {
    id: 'demo-scheduled',
    title: 'Demo Scheduled',
    icon: 'Calendar',
    color: 'primary',
    prospects: getFilteredProspects('demo-scheduled')
  },
  {
    id: 'in-negotiation',
    title: 'In Negotiation',
    icon: 'MessageSquare',
    color: 'secondary',
    prospects: getFilteredProspects('in-negotiation')
  },
  {
    id: 'closed-won',
    title: 'Closed-Won',
    icon: 'CheckCircle2',
    color: 'success',
    prospects: getFilteredProspects('closed-won')
  }];


  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-0 md:ml-20' : 'ml-0 md:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-primary to-secondary rounded-2xl">
                  <Icon name="Kanban" size={28} color="#FFFFFF" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground">Sales Pipeline</h1>
                  <p className="text-sm text-muted-foreground">Manage prospects through pipeline stages</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName={showAnalytics ? 'ChevronRight' : 'ChevronLeft'}
                iconPosition="right"
                onClick={() => setShowAnalytics(!showAnalytics)}>

                {showAnalytics ? 'Hide' : 'Show'} Analytics
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <FilterBar filters={filters} onFilterChange={setFilters} />

          {/* Kanban Board */}
          <div className="flex gap-4 lg:gap-6">
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-4 min-w-max pb-4">
                {columns?.map((column) =>
                <KanbanColumn
                  key={column?.id}
                  column={column}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(column?.id)}>

                    {column?.prospects?.map((prospect) =>
                  <ProspectCard
                    key={prospect?.id}
                    prospect={prospect}
                    onDragStart={() => handleDragStart(prospect)}
                    onViewDetails={() => handleViewDetails(prospect)}
                    calculateTimeInStage={calculateTimeInStage} />

                  )}
                  </KanbanColumn>
                )}
              </div>
            </div>

            {/* Analytics Sidebar */}
            {showAnalytics &&
            <AnalyticsSidebar prospects={prospects} />
            }
          </div>
        </div>
      </div>

      {/* Prospect Detail Modal */}
      {showDetailModal && selectedProspect &&
      <ProspectDetailModal
        prospect={selectedProspect}
        onClose={() => setShowDetailModal(false)}
        onUpdate={handleUpdateProspect} />

      }
    </div>);

};

export default SalesPipelineKanbanBoard;